import * as React from "react";
import { useAtomValue, useSetAtom } from "jotai";
import {
  addToastAtom,
  removeToastAtom,
  updateToastAtom,
} from "./atoms";
import type {
  ToastColor,
  ToastPosition,
  ToastData,
  ToastAction,
} from "./Toast.types";

export interface UseToastOptions {
  position?: ToastPosition;
  color?: ToastColor;
  icon?: React.ReactNode;
  title?: string;
  description?: string;
  actions?: ToastAction[];
  dismissable?: boolean;
  duration?: number; // milliseconds, undefined means persistent
}

export const useToast = () => {
  const addToast = useSetAtom(addToastAtom);
  const removeToast = useSetAtom(removeToastAtom);
  const updateToast = useSetAtom(updateToastAtom);

  const toast = React.useCallback(
    (options: UseToastOptions): string => {
      return addToast({
        position: options.position || "SE",
        color: options.color || "info",
        icon: options.icon,
        title: options.title,
        description: options.description,
        actions: options.actions,
        dismissable: options.dismissable !== false, // default true
        duration: options.duration,
      });
    },
    [addToast]
  );

  // Convenience methods for common toast types
  const success = React.useCallback(
    (
      title: string,
      description?: string,
      options?: Omit<UseToastOptions, "title" | "description" | "color">
    ): string => {
      return toast({
        ...options,
        title,
        description,
        color: "success",
      });
    },
    [toast]
  );

  const error = React.useCallback(
    (
      title: string,
      description?: string,
      options?: Omit<UseToastOptions, "title" | "description" | "color">
    ): string => {
      return toast({
        ...options,
        title,
        description,
        color: "danger",
      });
    },
    [toast]
  );

  const warning = React.useCallback(
    (
      title: string,
      description?: string,
      options?: Omit<UseToastOptions, "title" | "description" | "color">
    ): string => {
      return toast({
        ...options,
        title,
        description,
        color: "warning",
      });
    },
    [toast]
  );

  const info = React.useCallback(
    (
      title: string,
      description?: string,
      options?: Omit<UseToastOptions, "title" | "description" | "color">
    ): string => {
      return toast({
        ...options,
        title,
        description,
        color: "info",
      });
    },
    [toast]
  );

  return {
    toast,
    success,
    error,
    warning,
    info,
    dismiss: removeToast,
    update: updateToast,
  };
};

